import { useState } from 'react'
import { detectProfile } from '../utils/sectionProfiles'

// ── Mini intensity bar ────────────────────────────────────────────────────────

function IntensityBar({ label, value, color }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
        <span style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
        <span style={{ fontSize: '0.6rem', color: '#9ca3af', fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(value * 100)}%
        </span>
      </div>
      <div style={{ height: 3, borderRadius: 2, background: '#1e3a5f', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 2,
          width: `${value * 100}%`,
          background: color,
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  )
}

// ── Compact variant — just badge + mode tag, no bars ─────────────────────────

export function SectionProfileChip({ chapterTitle, chapterNumber }) {
  const profile = detectProfile(chapterTitle, chapterNumber)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px',
      borderRadius: 20,
      background: `${profile.color}14`,
      border: `1px solid ${profile.color}40`,
      fontSize: '0.68rem', fontWeight: 600, color: profile.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: profile.color, flexShrink: 0,
      }} />
      {profile.label}
    </span>
  )
}

// ── Full panel variant — badge + bars ─────────────────────────────────────────

export default function SectionProfileBadge({ chapterTitle, chapterNumber }) {
  const [showNoise,     setShowNoise]     = useState(false)
  const [showEmpirical, setShowEmpirical] = useState(false)
  const profile = detectProfile(chapterTitle, chapterNumber)

  return (
    <div style={{
      background: '#0d1f35',
      border: `1px solid ${profile.color}30`,
      borderLeft: `3px solid ${profile.color}`,
      borderRadius: 6,
      padding: '0.625rem 0.75rem',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: profile.color, flexShrink: 0,
          }} />
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, color: profile.color,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            {profile.label}
          </span>
        </div>
        <span style={{
          fontSize: '0.62rem', color: '#64748b',
          fontStyle: 'italic',
        }}>
          {profile.writingMode}
        </span>
      </div>

      {/* Writing intensity bars */}
      <IntensityBar label="Analytical"  value={profile.analyticalIntensity} color={profile.color} />
      <IntensityBar label="Abstraction" value={profile.abstractionLevel}    color={profile.color} />
      <IntensityBar label="Critique"    value={profile.critiqueIntensity}   color={profile.color} />

      {/* Noise debug toggle */}
      <button
        onClick={() => setShowNoise(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '4px 0', marginTop: 6, color: '#475569',
          fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}
      >
        <span style={{
          width: 10, height: 10, borderRadius: 2,
          background: showNoise ? '#334155' : 'transparent',
          border: '1px solid #475569',
          display: 'inline-block', flexShrink: 0,
        }} />
        Noise layer
      </button>

      {showNoise && (
        <div style={{ marginTop: 2 }}>
          <div style={{ height: '1px', background: '#1e3a5f', margin: '4px 0 8px' }} />
          <IntensityBar label="Natural Variation" value={profile.noiseIntensity}     color='#94a3b8' />
          <IntensityBar label="Smoothness"        value={1 - profile.noiseIntensity} color='#94a3b8' />
          <IntensityBar label="Optimization"      value={profile.optimizationLevel}  color='#94a3b8' />
        </div>
      )}

      {/* Empirical realism toggle */}
      <button
        onClick={() => setShowEmpirical(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '4px 0', marginTop: 2, color: '#475569',
          fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}
      >
        <span style={{
          width: 10, height: 10, borderRadius: 2,
          background: showEmpirical ? '#334155' : 'transparent',
          border: '1px solid #475569',
          display: 'inline-block', flexShrink: 0,
        }} />
        Empirical layer
      </button>

      {showEmpirical && (
        <div style={{ marginTop: 2 }}>
          <div style={{ height: '1px', background: '#1e3a5f', margin: '4px 0 8px' }} />
          <IntensityBar label="Evidence Consistency"  value={profile.evidenceConsistency}       color='#f59e0b' />
          <IntensityBar label="Hypothesis Support"    value={profile.hypothesisSupportStrength} color='#f59e0b' />
          <IntensityBar label="Data Variability"      value={profile.dataVariability}           color='#f59e0b' />
          <IntensityBar label="Interp. Ambiguity"     value={profile.interpretiveAmbiguity}     color='#f59e0b' />
        </div>
      )}
    </div>
  )
}
